import React, { useEffect, useState, useMemo } from 'react'
import { InputPassword } from '../../../ui'
import { useTranslation } from 'react-i18next'
import { MAX_LENGTH, TARGET_PLATFORMS } from '../../../constants/constants'
import { equals } from 'ramda'
import { useStore } from '../../../StateProvider'
import { tByPlatform } from '../../../i18n/i18nUtils'
import { useGetPlatform } from '../utils'
import { NewPasswordField } from './NewPasswordField/NewPasswordField'

const getIsPasswordValid = (value, regExp) => {
  if (!value || !regExp) return value?.length > 0
  return new RegExp(regExp).test(value)
}

const PASSWORD = 'password'
const REPEAT_PASSWORD = 'repeatPassword'

/**
 * @param {{name: string}} props
 */
const ExternalPasswordFields = ({ name }) => {
  const { t } = useTranslation()
  const [{ whitelabel: { passwordExpressionByPlatform, generatePasswordForMtPlatform } }, dispatch] = useStore()
  const [password, setPassword] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [repeatPasswordError, setRepeatPasswordError] = useState('')
  const [passwordValidation, setPasswordValidation] = useState(false)
  const [repeatPasswordValidation, setRepeatPasswordValidation] = useState(false)
  const platform = useGetPlatform()
  const isShown = useMemo(() => platform === TARGET_PLATFORMS.MT4 ? !generatePasswordForMtPlatform : platform !== TARGET_PLATFORMS.MKTD, [platform, generatePasswordForMtPlatform])

  useEffect(() => {
    if (isShown) {
      checkPassword(password)
      return
    }
    dispatch({ type: 'removeValidationFld', name })
  }, [platform, name, isShown])

  const onChangeNewPassword = (value, { target: { name } }) => {
    setPassword(value)
    dispatch({ type: 'setExtFieldValue', name, value })
  }

  const onChangeConfirmPassword = ({ target: { value, name } }) => {
    setRepeatPassword(value)
  }

  const checkPassword = (value) => {
    const expression = passwordExpressionByPlatform[platform]
    const isPasswordValid = getIsPasswordValid(value, expression?.pattern)
    const isRepeatPasswordValid = !!repeatPassword.length && equals(value, repeatPassword)
    setPasswordValidation(isPasswordValid)
    dispatch({ type: 'setValidationFld', name: PASSWORD, value: isPasswordValid && isRepeatPasswordValid })
    if (repeatPassword.length) repeatPasswordBlur(repeatPassword)
  }

  const onPasswordBlur = ({ target: { value } }) => {
    checkPassword(value)
  }

  const onRepeatPasswordBlur = ({ target: { value } }) => repeatPasswordBlur(value)

  const repeatPasswordBlur = (value) => {
    const expression = passwordExpressionByPlatform[platform]
    const isPasswordValid = getIsPasswordValid(value, expression?.pattern)
    const isRepeatPasswordValid = !!value.length && equals(value, password)
    setRepeatPasswordValidation(isRepeatPasswordValid)
    setRepeatPasswordError(
      equals(value, password) || !value.length
        ? ''
        : 'passwordField.unmatchedPasswords'
    )
    dispatch({
      type: 'setValidationFld', name: PASSWORD, value: isPasswordValid && isRepeatPasswordValid
    })
  }

  return isShown ? (
    <>
      <NewPasswordField
        id={PASSWORD}
        passwordRequirements={passwordExpressionByPlatform[platform]}
        label={t(tByPlatform(platform, 'passwordField.password'))}
        onChange={onChangeNewPassword}
        valid={passwordValidation}
        onBlur={onPasswordBlur}
        key={PASSWORD}
      />
      <InputPassword
        value={repeatPassword}
        label={t('passwordField.confirm')}
        error={t(repeatPasswordError)}
        name={REPEAT_PASSWORD}
        onChange={onChangeConfirmPassword}
        onBlur={onRepeatPasswordBlur}
        maxLength={MAX_LENGTH}
        valid={repeatPasswordValidation}
        key={REPEAT_PASSWORD}
        required
      />
    </>
  ) : null
}

const getExternalPasswordFields = ({ order }) => {
  return (
    <ExternalPasswordFields
      key={PASSWORD}
      name={PASSWORD}
      order={order}
    />
  )
}

export { ExternalPasswordFields, getExternalPasswordFields }
